/**
 * TemplateRenderer - Renders card data onto canvas textures
 * Handles text, images, and all layer compositing
 */

import {
  CANVAS_TEXTURE_WIDTH,
  CANVAS_TEXTURE_HEIGHT,
  TEXT_SHADOW_DEFAULT
} from '@config/constants.js';

export class TemplateRenderer {
  constructor() {
    this.canvas = document.createElement('canvas');
    this.canvas.width = CANVAS_TEXTURE_WIDTH;
    this.canvas.height = CANVAS_TEXTURE_HEIGHT;
    this.ctx = this.canvas.getContext('2d', { alpha: true }); // Enable alpha channel

    // Enable high-quality rendering
    this.ctx.imageSmoothingEnabled = true;
    this.ctx.imageSmoothingQuality = 'high';

    console.log('[TemplateRenderer] Canvas created:', this.canvas.width, 'x', this.canvas.height);
  }

  /**
   * Render a card with template and card state
   * @param {HTMLImageElement} templateImage - Base template image
   * @param {Object} template - Template configuration
   * @param {Object} cardState - Card state with properties
   * @param {string} side - 'front' or 'back'
   * @returns {HTMLCanvasElement}
   */
  render(templateImage, template, cardState, side = 'front') {
    // Back side is always upgraded
    const isUpgraded = side === 'back' ? true : cardState.upgraded;
    // Clear canvas with full transparency
    this.ctx.clearRect(0, 0, this.canvas.width, this.canvas.height);

    // Get layers for this side
    const layers = template.sides[side]?.layers || [];

    // First pass: Render image layers (artwork) - these go behind the template
    layers.forEach(layer => {
      if (layer.type === 'image') {
        try {
          this._renderImageLayer(layer, cardState, side);
        } catch (error) {
          console.error(`[TemplateRenderer] Error rendering image layer ${layer.id}:`, error);
        }
      }
    });

    // Draw base template image (PNG with transparency) - this goes on top of artwork
    if (templateImage) {
      this.ctx.drawImage(templateImage, 0, 0, this.canvas.width, this.canvas.height);
    } else {
      // Fallback: draw a semi-transparent background if no template
      this.ctx.fillStyle = 'rgba(42, 42, 42, 0.8)';
      this.ctx.fillRect(0, 0, this.canvas.width, this.canvas.height);
    }

    // Second pass: Render text and icon layers - these go on top of the template
    layers.forEach(layer => {
      try {
        switch (layer.type) {
          case 'text':
            this._renderTextLayer(layer, cardState, isUpgraded);
            break;
          case 'icon':
            this._renderIconLayer(layer, cardState);
            break;
          case 'image':
            // Already rendered in first pass
            break;
          default:
            console.warn(`[TemplateRenderer] Unknown layer type: ${layer.type}`);
        }
      } catch (error) {
        console.error(`[TemplateRenderer] Error rendering layer ${layer.id}:`, error);
      }
    });

    // Debug: Show canvas in development (optional)
    // if (import.meta.env.DEV) {
    //   this._debugCanvas(side);
    // }

    return this.canvas;
  }

  /**
   * Render a text layer
   * @private
   */
  _renderTextLayer(layer, cardState, isUpgraded = false) {
    const value = cardState[layer.id];

    if (value === undefined || value === null || value === '') {
      return;
    }

    let text = String(value);

    // Handle upgraded cards - add + to name and change color
    const isCardNameLayer = layer.id === 'cardName' || layer.id === 'cardNameBack';
    if (isCardNameLayer && isUpgraded) {
      text = text + '+';
    }

    // Set font with Arial fallback
    const fontWithFallback = layer.font.replace('Kreon', 'Arial');
    this.ctx.font = fontWithFallback;
    this.ctx.textAlign = layer.align || 'left';
    this.ctx.textBaseline = layer.textBaseline || 'alphabetic';

    // Determine color (handle upgraded card name)
    let color = layer.color;
    if (isCardNameLayer && isUpgraded && layer.colorUpgraded) {
      color = layer.colorUpgraded;
    }
    this.ctx.fillStyle = color;

    // Apply text shadow if defined
    if (layer.shadow) {
      this.ctx.shadowColor = layer.shadow.color || TEXT_SHADOW_DEFAULT.color;
      this.ctx.shadowBlur = layer.shadow.blur || TEXT_SHADOW_DEFAULT.blur;
      this.ctx.shadowOffsetX = layer.shadow.offsetX || TEXT_SHADOW_DEFAULT.offsetX;
      this.ctx.shadowOffsetY = layer.shadow.offsetY || TEXT_SHADOW_DEFAULT.offsetY;
    }

    // Render text based on layer configuration
    if (layer.lineHeight && layer.maxWidth) {
      this._renderMultilineText(text, layer.position.x, layer.position.y, layer.maxWidth, layer.lineHeight);
    } else if (layer.maxWidth) {
      this._renderFittedText(text, layer.position.x, layer.position.y, layer.maxWidth);
    } else {
      this.ctx.fillText(text, layer.position.x, layer.position.y);
    }

    // Reset shadow
    this.ctx.shadowColor = 'transparent';
    this.ctx.shadowBlur = 0;
    this.ctx.shadowOffsetX = 0;
    this.ctx.shadowOffsetY = 0;
  }

  /**
   * Render text that fits within a max width
   * @private
   */
  _renderFittedText(text, x, y, maxWidth) {
    const metrics = this.ctx.measureText(text);

    if (metrics.width > maxWidth) {
      // Scale down to fit
      const scale = maxWidth / metrics.width;
      this.ctx.save();
      this.ctx.translate(x, y);
      this.ctx.scale(scale, 1);
      this.ctx.fillText(text, 0, 0);
      this.ctx.restore();
    } else {
      this.ctx.fillText(text, x, y);
    }
  }

  /**
   * Render multi-line text with word wrapping
   * @private
   */
  _renderMultilineText(text, x, y, maxWidth, lineHeight) {
    const words = text.split(' ');
    const lines = [];
    let currentLine = '';

    // Break text into lines
    words.forEach(word => {
      const testLine = currentLine ? `${currentLine} ${word}` : word;
      const metrics = this.ctx.measureText(testLine);

      if (metrics.width > maxWidth && currentLine) {
        lines.push(currentLine);
        currentLine = word;
      } else {
        currentLine = testLine;
      }
    });

    if (currentLine) {
      lines.push(currentLine);
    }

    // Render each line
    lines.forEach((line, index) => {
      const lineY = y + (index * lineHeight);
      this.ctx.fillText(line, x, lineY);
    });
  }

  /**
   * Render an image layer (artwork)
   * @private
   */
  _renderImageLayer(layer, cardState, side = 'front') {
    // Get artwork based on layer id
    const artwork = cardState.artwork;

    // Try to get artwork for the current side, fallback to front
    const image = (artwork && artwork[side]) || (artwork && artwork.front);

    if (!image) {
      // No artwork available, skip rendering
      return;
    }

    if (!image || !image.complete || !image.naturalWidth) {
      console.warn('[TemplateRenderer] Image not loaded yet:', layer.id);
      return;
    }

    // Get layer configuration
    const { position, size, fit = 'cover' } = layer;
    const targetX = position.x;
    const targetY = position.y;
    const targetWidth = size.width;
    const targetHeight = size.height;

    // Calculate source dimensions based on fit mode
    let sx = 0;
    let sy = 0;
    let sw = image.naturalWidth;
    let sh = image.naturalHeight;
    let dx = targetX;
    let dy = targetY;
    let dw = targetWidth;
    let dh = targetHeight;

    const imageAspect = image.naturalWidth / image.naturalHeight;
    const targetAspect = targetWidth / targetHeight;

    if (fit === 'cover') {
      // Cover: fill the entire target area, cropping if necessary
      if (imageAspect > targetAspect) {
        // Image is wider than target - crop width
        sw = image.naturalHeight * targetAspect;
        sx = (image.naturalWidth - sw) / 2;
      } else {
        // Image is taller than target - crop height
        sh = image.naturalWidth / targetAspect;
        sy = (image.naturalHeight - sh) / 2;
      }
    } else if (fit === 'contain') {
      // Contain: fit entire image within target area, letterboxing if necessary
      if (imageAspect > targetAspect) {
        // Image is wider - fit to width
        dh = targetWidth / imageAspect;
        dy = targetY + (targetHeight - dh) / 2;
      } else {
        // Image is taller - fit to height
        dw = targetHeight * imageAspect;
        dx = targetX + (targetWidth - dw) / 2;
      }
    }
    // else 'fill' - stretch to fit (default behavior with simple drawImage)

    // Save context state
    this.ctx.save();

    try {
      // Draw the image
      this.ctx.drawImage(
        image,
        sx, sy, sw, sh,  // source rectangle
        dx, dy, dw, dh   // destination rectangle
      );
    } catch (error) {
      console.error('[TemplateRenderer] Failed to draw image:', error);
    }

    // Restore context state
    this.ctx.restore();
  }

  /**
   * Render an icon layer (e.g., energy cost circle)
   * @private
   */
  _renderIconLayer(layer, cardState) {
    // Icons are typically rendered as part of the template image
    // Or as dynamic overlays - implementation depends on specific needs
    console.log('[TemplateRenderer] Icon layer rendering placeholder:', layer.id);
  }

  /**
   * Get the canvas element
   * @returns {HTMLCanvasElement}
   */
  getCanvas() {
    return this.canvas;
  }

  /**
   * Get the 2D context
   * @returns {CanvasRenderingContext2D}
   */
  getContext() {
    return this.ctx;
  }

  /**
   * Get canvas as data URL
   * @param {string} format - Image format (default: 'image/png')
   * @param {number} quality - Image quality 0-1 (for JPEG)
   * @returns {string}
   */
  toDataURL(format = 'image/png', quality = 1.0) {
    return this.canvas.toDataURL(format, quality);
  }

  /**
   * Get canvas as Blob
   * @param {string} format - Image format (default: 'image/png')
   * @param {number} quality - Image quality 0-1 (for JPEG)
   * @returns {Promise<Blob>}
   */
  toBlob(format = 'image/png', quality = 1.0) {
    return new Promise((resolve, reject) => {
      this.canvas.toBlob(
        blob => {
          if (blob) {
            resolve(blob);
          } else {
            reject(new Error('Failed to create blob from canvas'));
          }
        },
        format,
        quality
      );
    });
  }

  /**
   * Resize canvas (useful for high-res export)
   * @param {number} width - New width
   * @param {number} height - New height
   */
  resize(width, height) {
    this.canvas.width = width;
    this.canvas.height = height;
    this.ctx = this.canvas.getContext('2d', { alpha: false });
    this.ctx.imageSmoothingEnabled = true;
    this.ctx.imageSmoothingQuality = 'high';
    console.log('[TemplateRenderer] Canvas resized to:', width, 'x', height);
  }

  /**
   * Reset canvas to default size
   */
  resetSize() {
    this.resize(CANVAS_TEXTURE_WIDTH, CANVAS_TEXTURE_HEIGHT);
  }

  /**
   * Clear the canvas
   */
  clear() {
    this.ctx.clearRect(0, 0, this.canvas.width, this.canvas.height);
  }

  /**
   * Debug canvas rendering (development only)
   * @private
   */
  _debugCanvas(side) {
    // Create a small preview in the page
    const existingDebug = document.getElementById('canvas-debug');
    if (existingDebug) {
      existingDebug.remove();
    }

    const debugDiv = document.createElement('div');
    debugDiv.id = 'canvas-debug';
    debugDiv.style.cssText = `
      position: fixed;
      bottom: 10px;
      left: 10px;
      z-index: 10000;
      background: rgba(0,0,0,0.9);
      padding: 10px;
      border: 2px solid #4a90e2;
      border-radius: 4px;
      max-width: 200px;
    `;

    const title = document.createElement('div');
    title.textContent = `Canvas Preview (${side})`;
    title.style.cssText = `
      color: white;
      font-size: 12px;
      margin-bottom: 5px;
      font-family: monospace;
    `;

    const canvasCopy = document.createElement('canvas');
    canvasCopy.width = 200;
    canvasCopy.height = 300;
    canvasCopy.style.cssText = `
      width: 100%;
      border: 1px solid #666;
    `;

    const ctx = canvasCopy.getContext('2d');
    ctx.drawImage(this.canvas, 0, 0, 200, 300);

    debugDiv.appendChild(title);
    debugDiv.appendChild(canvasCopy);
    document.body.appendChild(debugDiv);

    console.log('[TemplateRenderer] Canvas debug preview added to page');
  }

  /**
   * Dispose of resources
   */
  dispose() {
    this.clear();
    this.canvas = null;
    this.ctx = null;
    console.log('[TemplateRenderer] Disposed');
  }
}

// Create singleton instance
export const templateRenderer = new TemplateRenderer();

console.log('TemplateRenderer loaded');
